<template>
	<I2FormRow>
		<I2FormField label="Assessment Name" helpElementName="AssessmentName">
			<InputText
				v-model="assessment.assessmentName"
				:class="{ 'p-invalid': saveAttempted && !assessment.assessmentName }"
				maxlength="200"
				v-bind="lock.input.value"
				v-tooltip="lock.tooltip"
			/>
		</I2FormField>
		<I2FormField helpElementName="AssessmentProductName">
			<template #label>
				<span class="flex items-center gap-2">
					Product Name
					<i
						v-if="isProductNameInactive"
						class="pi pi-exclamation-triangle text-yellow-500"
						v-tooltip="'This product is no longer active and cannot be selected in new assessments'"
					></i>
				</span>
			</template>
			<div class="flex items-center gap-2">
				<Select
					v-model="selectedProductName"
					:options="productNameList"
					optionLabel="valueTxt"
					placeholder="Select a Product Name"
					class="w-full md:w-14rem"
					:class="{ 'p-invalid': saveAttempted && selectedDevelopmentStage == null }"
					:filter="productNameList.length > 10"
					v-bind="lock.select.value"
					v-tooltip="lock.tooltip"
				></Select>
			</div>
		</I2FormField>
		<div class="flex flex-row items-center gap-2 justify-end">
			<Button
				class="btn-tiny"
				id="lockAssessment"
				v-if="
					SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update) ||
					SessionInfo.currentUser().role.indexOf('Client Admin') !== -1 ||
					SessionInfo.currentUser().role.indexOf('ERM Admin') !== -1
				"
				:disabled="
					assessment.isLocked &&
					!(
						assessment.lockedByPersonId === SessionInfo.currentUser().id ||
						SessionInfo.currentUserIsAdmin() ||
						SessionInfo.currentUser().role.indexOf('Client Admin') !== -1 ||
						SessionInfo.currentUser().role.indexOf('ERM Admin') !== -1
					)
				"
				@click="assessmentLockUnlock"
				v-tooltip="lock.tooltip"
			>
				{{ assessment.isLocked ? "Unlock Assessment" : "Lock Assessment" }}
			</Button>
		</div></I2FormRow
	><I2FormRow>
		<I2FormField label="Assessment Number" helpElementName="AssessmentNumber">
			<b>{{ assessment.asessementIncrementalNumber }}</b>
		</I2FormField>
		<I2FormField helpElementName="AssessmentDevelopmentStage">
			<template #label>
				<span class="flex items-center gap-2">
					Development Stage
					<i
						v-if="isDevelopmentStageInactive"
						class="pi pi-exclamation-triangle text-yellow-500"
						v-tooltip="'This development stage is no longer active and cannot be selected in new assessments'"
					></i>
				</span>
			</template>
			<Select
				v-model="selectedDevelopmentStage"
				:options="developmentStageList"
				optionLabel="valueTxt"
				placeholder="Select a Development Stage"
				class="w-full md:w-14rem"
				:class="{ 'p-invalid': saveAttempted && selectedDevelopmentStage == null }"
				:filter="developmentStageList.length > 10"
				v-bind="lock.select.value"
				v-tooltip="lock.tooltip"
			>
			</Select>
		</I2FormField>
		<I2FormField label="Electronic Lab Number (ELN)" helpElementName="AssessmentElectronicLabNumber">
			<InputText v-model="assessment.electronicLabNumber" maxlength="200" v-bind="lock.input.value" v-tooltip="lock.tooltip" /> </I2FormField
	></I2FormRow>
	<I2FormRow>
		<I2FormField label="Assessment Comments" helpElementName="AssessmentComments">
			<Textarea v-model="assessment.comments" rows="4" v-bind="lock.input.value" v-tooltip="lock.tooltip" />
		</I2FormField>
	</I2FormRow>
	<div class="flex justify-between w-full gap-2 mb-4" style="padding-top: 20px" v-if="assessment.id">
		<PageLabel :helpPageName="'ProjectParticipants'">Assessment Collaborators</PageLabel>
		<span class="flex gap-2">
			<Button
				class="btn-tiny"
				id="addParticipant"
				v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update) && !inActionAddCollaborator"
				@click="addCollaborator"
				v-bind="lock.button.value"
				v-tooltip="lock.tooltip"
			>
				Add Collaborator
			</Button>

			<div class="p-d-flex p-jc-between">
				<span class="relative">
					<i class="pi pi-search absolute inset-y-0 right-3 flex items-center pointer-events-none"></i>
					<AutoComplete
						:multiple="false"
						v-model="selectedPerson"
						:suggestions="filteredPersons"
						placeholder="Type person name"
						optionLabel="fullName"
						optionValue="id"
						@complete="searchPerson($event)"
						v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update) && inActionAddCollaborator"
						@item-select="selectPerson($event)"
						:id="'selectedPerson_' + props.tabId"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</span>
			</div>
			<Button
				class="btn-tiny"
				id="addParticipant"
				v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update) && inActionAddCollaborator"
				@click="cancelCollaborator"
				v-bind="lock.button.value"
				v-tooltip="lock.tooltip"
			>
				Cancel
			</Button>
		</span>
	</div>
	<div>
		<DataTable
			:value="assessment.assessmentCollaborators"
			:row-hover="true"
			v-if="assessment.id"
			responsiveLayout="scroll"
			:paginator="true"
			:rows="10"
			paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
			:rowsPerPageOptions="[10, 20, 50]"
			currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
			dataKey="id"
			v-model:filters="usersFilters"
			:loading="loading1"
		>
			<template #header>
				<div class="p-d-flex p-jc-between">
					<Button icon="pi pi-filter-slash" label="Clear" @click="usersFilters['global'].value = null" />
					<I2Label label="Keyword Search" helpElementName="AssessmentCollaboratorsKeywordSearch">
						<span class="relative">
							<i class="pi pi-search absolute inset-y-3 right-3 flex items-center pointer-events-none"></i>
							<InputText class="pl-10" v-model="usersFilters['global'].value" placeholder="Keyword Search" id="assetKeywordSearch" />
						</span>
					</I2Label>
				</div>
			</template>
			<template #empty>No Assessment Collaborators found. </template>
			<template #loading> Loading Assessment Collaborators data. Please wait. </template>
			<Column field="person.firstName" :sortable="true">
				<template #header><I2Label label="Name" helpElementName="AssessmentCollaboratorsName" /></template>
				<template #body="{ data }">
					<div class="w-full" v-if="data != null">{{ data.person.firstName + " " + data.person.lastName }}</div>
				</template>
				<template #filter="{ filterModel }">
					<InputText
						type="text"
						v-model="filterModel.value"
						class="p-column-filter"
						placeholder="Search by name"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</template>
			</Column>
			<Column field="person.emailId" :sortable="true">
				<template #header><I2Label label="Email" helpElementName="AssessmentCollaboratorsEmail" /></template>
				<template #body="{ data }">
					<div class="w-full" v-if="data != null">{{ data.person.emailId }}</div>
				</template>
				<template #filter="{ filterModel }">
					<InputText
						type="text"
						v-model="filterModel.value"
						class="p-column-filter"
						placeholder="Search by email id"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</template>
			</Column>
			<Column field="id" width="10%">
				<template #header><I2Label label="Action" helpElementName="AssessmentCollaboratorsAction" /></template>
				<template #body="{ data }">
					<div class="flex">
						<span style="cursor: pointer"
							><FontAwesomeIcon
								:icon="['far', 'trash']"
								title="Delete"
								@click="!assessmentStore.isLocked && removeCollaborator(data)"
								class="ml-2"
								v-bind="lock.button.value"
								v-tooltip="lock.tooltip"
						/></span>
						<!--@click="deleteParticipant(data.id, project.id)"-->
					</div>
				</template>
			</Column>
		</DataTable>
	</div>
	<div class="flex items-center justify-between w-full gap-2 mt-4">
		<div class="flex flex-row items-center gap-2">
			<Button
				id="deleteAssessment"
				severity="danger"
				v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update) && !inActionAddCollaborator"
				@click="assessmentDelete"
				v-bind="lock.button.value"
				v-tooltip="lock.tooltip"
			>
				Delete
			</Button>
		</div>
		<div class="flex flex-row items-center gap-2">
			<Button id="btnback" v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Read)" @click="backClick()"> Back </Button>
			<Button
				id="saveAssessment"
				v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update) && !inActionAddCollaborator"
				@click="saveAssessment"
				v-bind="lock.button.value"
				v-tooltip="lock.tooltip"
			>
				Save
			</Button>
		</div>
	</div>

	<Dialog v-model:visible="showDialogDelete" :modal="true" style="width: 35vw" :closable="false" contentStyle="overflow:visible">
		<template #header>
			<PageLabel :helpPageName="'AssessmentDelete'">Delete Assessment </PageLabel>
		</template>
		<FormCard>
			<I2FormRow class="mt-6">
				<h1>Are you sure you would like to delete selected Assessment?</h1>
			</I2FormRow>
		</FormCard>
		<template #footer>
			<I2FormRow right>
				<Button label="Cancel" class="inverted mr-4" @click="showDialogDelete = false" right />
				<Button label="Delete" class="ml-4" @click="deleteAssessmentOK()" right />
			</I2FormRow>
		</template>
	</Dialog>

	<Dialog v-model:visible="showDialogLock" :modal="true" style="width: 35vw" :closable="false">
		<FormCard>
			<I2FormRow class="mt-6">
				<h3>
					You are about to lock {{ assessment.assessmentName }}.<br />
					No changes can be made to a locked assessment, and it can only be unlocked by you or an administrator.
				</h3>
			</I2FormRow>
		</FormCard>
		<template #footer>
			<I2FormRow class="justify-start">
				<Button label="Cancel" class="p-button-danger p-button-sm mr-2" @click="showDialogLock = false" />
				<Button label="Lock" class="p-button-success p-button-sm" @click="lockUnlockAssessmentOK(true)" />
			</I2FormRow>
		</template>
	</Dialog>

	<Dialog v-model:visible="showDialogUnlock" :modal="true" style="width: 35vw" :closable="false">
		<FormCard>
			<I2FormRow class="mt-6">
				<h3>You are about to unlock {{ assessment.assessmentName }}.<br /></h3>
			</I2FormRow>
		</FormCard>
		<template #footer>
			<I2FormRow class="justify-start">
				<Button label="Cancel" class="p-button-danger p-button-sm mr-2" @click="showDialogUnlock = false" />
				<Button label="Unlock" class="p-button-success p-button-sm" @click="lockUnlockAssessmentOK(false)" />
			</I2FormRow>
		</template>
	</Dialog>

	<!-- <Toast position="top-right" /> -->
</template>

<script setup lang="ts">
import { ref, onMounted, watchEffect, reactive, nextTick, computed } from "vue"

import Button from "primevue/button"
import InputText from "primevue/inputtext"
import Textarea from "primevue/textarea"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import AutoComplete, { AutoCompleteCompleteEvent, AutoCompleteOptionSelectEvent } from "primevue/autocomplete"
import { ApiClient, Assessment, AssessmentCollaboratorPut, AssessmentCollaborators, Person, MasterDropdown } from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import { EventBus } from "../eventBus"
import { useRouter } from "vue-router"
import Dialog from "primevue/dialog"
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
import { useToast } from "primevue/usetoast"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faTrash } from "@fortawesome/pro-regular-svg-icons"
import { FilterMatchMode, FilterOperator } from "@primevue/core/api"
library.add(faTrash)
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { useErrorToaster, useToaster } from "@/composables/toaster"

import { useHistoryHelper } from "../composables/historyHelper"
import { useAssessmentStore } from "@/store/assessmentStore"
import { useAssessmentLock } from "@/composables/assessmentLock"
const assessmentStore = useAssessmentStore()
const lock = useAssessmentLock(assessmentStore)
const router = useRouter()
const selectedPerson = ref()
const filteredPersons = ref()
const inActionAddCollaborator = ref(false)
let toast = useToast()
const { updateAssessmentUpdatedByDate } = useHistoryHelper()
const props = defineProps({
	assessmentId: {
		type: String,
		required: true,
		default: "",
	},
	tabId: {
		type: String,
		required: true,
		default: "",
	},
})
const prevAssessmentId = ref(props.assessmentId)
const assessment = ref(new Assessment())
assessment.value.assessmentName = ""
const assessmentL = ref()
const saveAttempted = ref(false)
const selectedDevelopmentStage = ref()
const selectedProductName = ref()
const allDevelopmentStages = ref([] as MasterDropdown[])
const developmentStageList = computed(() => {
	const activeItems = allDevelopmentStages.value.filter((item) => item.isActive === true)
	if (selectedDevelopmentStage.value?.isActive === false) {
		activeItems.push(selectedDevelopmentStage.value)
	}
	return activeItems
})
const productNameList = ref([] as MasterDropdown[])
const getDone = ref(false)
//const filters1 = ref(null)
const global = ref(null)
const loading1 = ref(false)
const showDialogDelete = ref(false)
const showDialogLock = ref(false)
const showDialogUnlock = ref(false)
let assessmentAndCollaborators = new AssessmentCollaboratorPut()

let usersFilters = reactive({
	global: { value: null, matchMode: FilterMatchMode.CONTAINS },
	person: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	emailId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
})

onMounted(() => {
	// getAllDevelopmentStageEntries()
	// getAllProductNameEntries()

	EventBus.on("selectedAssessmentNameTab", (screenId) => {
		EventBus.emit("loadStepListBox_" + screenId, { screenId: screenId, generalInformationId: "00000000-0000-0000-0000-000000000000" })
	})
})
const getAllProductNameEntries = (): Promise<void> => {
	return new Promise((resolve, reject) => {
		apiClient
			.masterDropdownByCategoryKeyAll("ProductName")
			.then((res: any) => {
				productNameList.value = res
				addDisabledOptionIfUsed(productNameList.value)
				resolve()
			})
			.catch((error: any) => {
				reject(error)
			})
	})
}

const addDisabledOptionIfUsed = (list: MasterDropdown[]) => {
	const found = list.find((item) => item.id === selectedProductName.value.id && item.isActive === false)
	if (found) {
		list.push(found)
	}
}
const getAllDevelopmentStageEntries = (): Promise<void> => {
	return new Promise((resolve, reject) => {
		apiClient
			.masterDropdownByCategoryKeyAll("DevelopmentStage")
			.then((res: any) => {
				allDevelopmentStages.value = res
				resolve()
			})
			.catch((error: any) => {
				reject(error)
			})
	})
}
const getAssessment = (): Promise<void> => {
	return new Promise((resolve, reject) => {
		apiClient
			.getSingleAssessmentById(props.assessmentId)
			.then((result) => {
				assessment.value = result
				selectedDevelopmentStage.value = result.developmentStage
				selectedPerson.value = result.assessmentCollaborators
				selectedProductName.value = result.productName
				assessmentStore.setLockStatus(result.isLocked ?? false, result.lockedByPerson?.firstName + " " + result.lockedByPerson?.lastName)
				resolve()
			})
			.catch((error: any) => {
				reject(error)
			})
	})
}
watchEffect(() => {
	if (props.assessmentId !== null && props.assessmentId !== "" && getDone.value === false) {
		Promise.all([getAssessment()]).then(() => {
			getAllDevelopmentStageEntries()
			getAllProductNameEntries()
			getDone.value = true
		})
	}
})
const assessmentDelete = () => {
	showDialogDelete.value = true
}

const validateRequired = (obj: any, requiredFields: Array<string>) => {
	let noMissingFields = true

	requiredFields.forEach((field) => {
		if (!obj[field] || obj[field]?.length == 0) {
			noMissingFields = false
		}
	})

	if (!noMissingFields) {
		useToaster({
			summary: "Missing Required Fields",
			detail: "Please fill in all required fields.",
			severity: "error",
			life: 3000,
		})
	}

	return noMissingFields
}

const saveAssessment = () => {
	saveAttempted.value = true
	if (!validateRequired(assessment.value, ["assessmentName", "developmentStageId", "productNameId"])) {
		return
	}

	var theCollaborators = Array<Person>()
	assessment.value.assessmentCollaborators?.forEach((person) => {
		if (person.person) theCollaborators.push(person.person)
	})
	assessmentAndCollaborators.collaborators = theCollaborators //assessment.value.assessmentCollaborators
	assessmentAndCollaborators.assessment = assessment.value
	assessmentAndCollaborators.assessment.developmentStageId = selectedDevelopmentStage.value.id
	assessmentAndCollaborators.assessment.productNameId = selectedProductName.value.id
	apiClient.postAssessmentCollaborators(assessmentAndCollaborators).then(
		(updatedAssessment) => {
			// Use the returned assessment data directly - no need for additional fetch
			toast.add({
				severity: "success",
				summary: "Assessment saved",
				detail: ``,
				life: 3000,
			})
			updateAssessmentUpdatedByDate(updatedAssessment.id)
		},
		(err) => {
			toast.add({
				severity: "warn",
				summary: "Assessment not saved",
				detail: ``,
				life: 3000,
			})
		}
	)
}
const deleteAssessmentOK = () => {
	apiClient.assessmentDelete(assessment.value.id).then(
		(result) => {
			//go back
			EventBus.emit("actionInBreadcrumb", { type: "subproject", key: assessment.value.subProjectId })
			showDialogDelete.value = false
		},
		(err) => {
			throw err
		}
	)
}
const lockUnlockAssessmentOK = (shouldLock: boolean) => {
	apiClient.assessmentLockUnlock(assessment.value.id, shouldLock).then(
		(result) => {
			EventBus.emit("actionInBreadcrumb", { type: "subproject", key: assessment.value.subProjectId })
			assessmentStore.setLockStatus(shouldLock, SessionInfo.currentUser().firstName + " " + SessionInfo.currentUser().lastName)
			showDialogLock.value = false
			showDialogUnlock.value = false
		},
		(err) => {
			throw err
		}
	)
}
const saveCollaborator = () => {
	var singleCollaborator = new AssessmentCollaborators()
	singleCollaborator.id = self.crypto.randomUUID()
	singleCollaborator.assessmentId = assessment.value.id
	singleCollaborator.personId = selectedPerson.value.id
	apiClient.addCollaboratorInAssessment(singleCollaborator).then((result) => {
		assessment.value.assessmentCollaborators?.push(result)
		inActionAddCollaborator.value = false
	})
}
const cancelCollaborator = () => {
	selectedPerson.value = []
	inActionAddCollaborator.value = false
}

function searchPerson(event: AutoCompleteCompleteEvent) {
	apiClient.filterPersonForCollaborators(event.query).then((res) => {
		let personWithFullName = res.map((person) => ({
			...person,
			fullName: `${person.firstName} ${person.lastName}`,
		}))

		if (assessment.value.assessmentCollaborators)
			personWithFullName = personWithFullName.filter(
				(person) => !assessment.value.assessmentCollaborators!.some((selected: AssessmentCollaborators) => selected.person!.id === person.id)
			)

		filteredPersons.value = personWithFullName
	})
}
const selectPerson = (event: AutoCompleteOptionSelectEvent) => {
	saveCollaborator()
}
const removeCollaborator = (collaborator: any) => {
	apiClient.removeCollaboratorFromAssessment(collaborator.id).then((result) => {
		if (assessment.value.assessmentCollaborators !== undefined)
			assessment.value.assessmentCollaborators = assessment.value.assessmentCollaborators.filter(
				(singleCollaborator) => singleCollaborator.id !== collaborator.id
			)
	})
}

const addCollaborator = () => {
	selectedPerson.value = []
	inActionAddCollaborator.value = true
	nextTick()
	var rootElement = document.getElementById("selectedPerson_" + props.tabId)
	if (rootElement) {
		rootElement.classList.add("p-focus")
		rootElement.classList.add("p-inputwrapper-focus")
	}
}

const backClick = () => {
	if (localStorage.getItem("selectedSubProjectId")) {
		let selectedSubProjectId = JSON.parse(localStorage.getItem("selectedSubProjectId")!)
		router.push({ name: "assessmentList", query: { subProjectId: selectedSubProjectId } })
	} else {
		router.push({ name: "projectList" })
	}
}

const isProductNameInactive = computed(() => {
	if (!selectedProductName.value) return false
	return productNameList.value.find((item) => item.id === selectedProductName.value.id && item.isActive === false) !== undefined
})

const isDevelopmentStageInactive = computed(() => {
	if (!selectedDevelopmentStage.value) return false
	return developmentStageList.value.find((item) => item.id === selectedDevelopmentStage.value.id && item.isActive === false) !== undefined
})

const assessmentLockUnlock = () => {
	if (assessment.value.isLocked) {
		showDialogUnlock.value = true // Open unlock dialog
	} else {
		showDialogLock.value = true // Open lock dialog
	}
}
</script>
<style scoped>
/* .btn-outline {
	background-color: white;
	color: #007bff;
	border: 2px solid #007bff;
	padding: 5px 10px;
	font-size: 12px;
	border-radius: 3px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.btn-outline:hover {
	background-color: #007bff;
	color: white;
} */
</style>
